import React, { JSX, useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router'
import { ArrowPathIcon, StopCircleIcon } from '@heroicons/react/24/outline'
import { LoadingSpinner } from 'components/Loading'
import { ErrorInline } from 'components/ErrorInline'
import { graphqlRequest, formatLocalDateTime } from 'lib/helpers'
import { eventPath } from 'lib/paths'
import { createRequestRace } from 'lib/adminListState'
import {
  YouTubeEffectiveSpeedLevel,
  YouTubeLiveSession,
  YouTubeSpeedLevel,
  YouTubeSpeedPanel as YouTubeSpeedPanelData,
  YouTubeStaffStopResult
} from 'types/api'
import getYouTubeSpeedPanelQuery from 'graphql/getYouTubeSpeedPanel.graphql'
import setYouTubeSpeedSettingsMutation from 'graphql/setYouTubeSpeedSettings.graphql'
import stopYouTubeSessionAsStaffMutation from 'graphql/stopYouTubeSessionAsStaff.graphql'

export const SETTABLE_SPEED_LEVELS: YouTubeSpeedLevel[] = ['ECONOMY', 'STANDARD', 'FAST', 'LIVE']

const LEVEL_LABEL: Record<YouTubeEffectiveSpeedLevel, string> = {
  SAVER: 'Saver',
  ECONOMY: 'Economy',
  STANDARD: 'Standard',
  FAST: 'Fast',
  LIVE: 'Live'
}

export const speedLevelLabel = (level: string | null): string => {
  if (level == null || level === '') return 'Unknown'
  return LEVEL_LABEL[level as YouTubeEffectiveSpeedLevel] ?? level
}

export const isSettableSpeedLevel = (level: string | null): level is YouTubeSpeedLevel =>
  level != null && (SETTABLE_SPEED_LEVELS as string[]).includes(level)

const LEVEL_LEGEND: Array<[YouTubeEffectiveSpeedLevel, string]> = [
  ['ECONOMY', '45 s while votes are open, 45 s for chat, 90 s when quiet.'],
  ['STANDARD', '15 s while votes are open, 45 s for chat, 90 s when quiet.'],
  ['FAST', '10 s while votes are open, 30 s for chat, 90 s when quiet.'],
  ['LIVE', 'Google\'s suggested wait (never under 5 s) while votes are open, 15 s for chat, 60 s when quiet.'],
  ['SAVER', '90 s everywhere. Used only by the budget brake.']
]

export const LIVE_BUDGET_COPY = 'Live needs a daily budget of 50,000 units or more.'
const BUDGET_COPY = 'The daily budget must be a whole number of units from 1,000 to 10,000,000, or empty to use the server setting.'
const HOLDBACK_COPY = 'Demo hold-back slots must be a whole number from 0 to 8.'
const APP_LIMIT_COPY = 'The app-wide chat limit must be a whole number from 0 to 8, or empty to use the server setting. Going above 8 needs the poller alarm revisited first.'
const ACCOUNT_LIMIT_COPY = 'The per-account chat limit must be a whole number from 0 to 8, or empty to use the server setting.'
const LOAD_ERROR_COPY = 'Couldn\'t load YouTube speed and limits.'

const LIVE_MIN_BUDGET_UNITS = 50000
const LIMIT_MAX = 8

const PT_TIME = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Los_Angeles', hour: 'numeric', minute: '2-digit' })

const ptTimeOf = (iso: string | null): string | null => {
  if (iso == null) return null
  const ms = Date.parse(iso)
  if (Number.isNaN(ms)) return null
  const raw = PT_TIME.format(new Date(ms))
  return `${raw.replace(/[\s\u202f]*(AM|PM)$/i, (_m, p: string) => ` ${p.toLowerCase()}`)} PT`
}

const untilCopy = (targetMs: number, nowMs: number): string => {
  const minutes = Math.floor(Math.max(0, targetMs - nowMs) / 60000)
  if (minutes < 1) return 'under a minute'
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (h === 0) return `${m} min`
  return m === 0 ? `${h} h` : `${h} h ${m} min`
}

const fmtCount = (n: number | null): string => (n == null ? 'unknown' : n.toLocaleString())

interface Draft {
  level: string
  brake: boolean
  budget: string
  holdback: string
  appLimit: string
  accountLimit: string
}
type DraftKey = keyof Draft
interface FormState {
  values: Draft
  touched: Partial<Record<DraftKey, true>>
  base: string | null
}

const draftOf = (p: YouTubeSpeedPanelData): Draft => ({
  level: isSettableSpeedLevel(p.defaultLevel) ? p.defaultLevel : 'ECONOMY',
  brake: p.brakeEnabled !== false,
  budget: p.dailyBudgetSaved === true && p.dailyBudgetUnits != null ? String(p.dailyBudgetUnits) : '',
  holdback: String(p.demoHoldbackSlots ?? 0),
  appLimit: p.appChatLimitSaved === true && p.appChatLimit != null ? String(p.appChatLimit) : '',
  accountLimit: p.accountChatLimitSaved === true && p.accountChatLimit != null ? String(p.accountChatLimit) : ''
})

const adoptPanel = (prev: FormState | null, p: YouTubeSpeedPanelData, reset: boolean): FormState => {
  if (prev == null || reset || Object.keys(prev.touched).length === 0) {
    return { values: draftOf(p), touched: {}, base: p.updatedAt ?? null }
  }
  return prev
}

const parseWhole = (raw: string): number | null => {
  const t = raw.replace(/[,\s_]/g, '')
  if (t === '') return null
  if (!/^\d{1,9}$/.test(t)) return Number.NaN
  return Number.parseInt(t, 10)
}

const inRange = (n: number | null, min: number, max: number): boolean =>
  n != null && Number.isInteger(n) && n >= min && n <= max

const withoutStopped = (p: YouTubeSpeedPanelData, gone: Set<string>): YouTubeSpeedPanelData => {
  if (gone.size === 0 || p.sessions == null) return p
  return { ...p, sessions: p.sessions.filter((s) => s.sessionId == null || !gone.has(s.sessionId)) }
}

interface SaveVariables {
  defaultLevel: string
  brakeEnabled: boolean
  dailyBudgetUnits: number | null
  demoHoldbackSlots: number
  appChatLimit: number | null
  accountChatLimit: number | null
  expectedUpdatedAt: string | null
}

const snapshotOf = (form: FormState): { variables: SaveVariables } | { error: string } => {
  const v = form.values
  if (!isSettableSpeedLevel(v.level)) return { error: 'Choose a speed level: Economy, Standard, Fast or Live.' }
  const budget = parseWhole(v.budget)
  if (budget != null && !inRange(budget, 1000, 10000000)) return { error: BUDGET_COPY }
  const holdback = parseWhole(v.holdback)
  if (holdback == null || !inRange(holdback, 0, LIMIT_MAX)) return { error: HOLDBACK_COPY }
  const appLimit = parseWhole(v.appLimit)
  if (appLimit != null && !inRange(appLimit, 0, LIMIT_MAX)) return { error: APP_LIMIT_COPY }
  const accountLimit = parseWhole(v.accountLimit)
  if (accountLimit != null && !inRange(accountLimit, 0, LIMIT_MAX)) return { error: ACCOUNT_LIMIT_COPY }
  if (v.level === 'LIVE' && budget != null && budget < LIVE_MIN_BUDGET_UNITS) return { error: LIVE_BUDGET_COPY }
  return {
    variables: {
      defaultLevel: v.level,
      brakeEnabled: v.brake,
      dailyBudgetUnits: budget,
      demoHoldbackSlots: holdback,
      appChatLimit: appLimit,
      accountChatLimit: accountLimit,
      expectedUpdatedAt: form.base
    }
  }
}

const liveAllowedFor = (form: FormState, p: YouTubeSpeedPanelData): boolean | null => {
  const budget = parseWhole(form.values.budget)
  if (budget != null) return Number.isFinite(budget) && budget >= LIVE_MIN_BUDGET_UNITS
  if (p.dailyBudgetSaved === true) return null
  return p.liveAllowed === true
}

const RefreshButton = ({ onClick, busy, disabled, testid }: { onClick: () => void, busy: boolean, disabled: boolean, testid: string }): JSX.Element => (
  <button
    type='button'
    onClick={onClick}
    disabled={disabled}
    data-testid={testid}
    title='Refresh YouTube speed, limits and sessions'
    className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
  >
    <ArrowPathIcon className={`size-3.5 ${busy ? 'animate-spin' : ''}`} aria-hidden='true' />
    Refresh
  </button>
)

const NumberField = ({
  id,
  label,
  value,
  onChange,
  placeholder,
  hint,
  disabled,
  testid
}: {
  id: string
  label: string
  value: string
  onChange: (v: string) => void
  placeholder: string
  hint: string
  disabled: boolean
  testid: string
}): JSX.Element => (
  <div className='flex min-w-0 flex-col gap-1'>
    <label htmlFor={id} className='text-xs font-medium text-dm-text-secondary'>{label}</label>
    <input
      id={id}
      type='text'
      inputMode='numeric'
      autoComplete='off'
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      data-testid={testid}
      className='w-full rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand disabled:opacity-60'
    />
    <p className='text-[11px] text-dm-text-accent' data-testid={`${testid}-hint`}>{hint}</p>
  </div>
)

const fallbackHint = (saved: boolean, value: number | null, unit: string): string => {
  if (saved) return 'Saved here. Leave it empty to use the server setting.'
  return value == null
    ? 'Empty uses the server setting.'
    : `Empty uses the server setting (${value.toLocaleString()}${unit}).`
}

const brakeLine = (p: YouTubeSpeedPanelData): string => {
  if (p.brakeEnabled === false) return 'Brake: off. Speeds never drop for the budget.'
  if (p.brakeCapLevel != null) {
    const since = ptTimeOf(p.brakeCapSince)
    return since != null
      ? `Brake: capped at ${speedLevelLabel(p.brakeCapLevel)} since ${since}.`
      : `Brake: capped at ${speedLevelLabel(p.brakeCapLevel)}.`
  }
  return 'Brake: on, not capping today.'
}

const todayLine = (p: YouTubeSpeedPanelData, nowMs: number): string => {
  const units = p.unitsToday ?? 0
  const budget = p.dailyBudgetUnits ?? 0
  const pct = budget > 0 ? Math.round((units * 100) / budget) : 0
  const resetMs = p.resetsAt != null ? Date.parse(p.resetsAt) : Number.NaN
  const reset = Number.isNaN(resetMs)
    ? 'Resets at midnight Pacific.'
    : `Resets at midnight Pacific, in ${untilCopy(resetMs, nowMs)}.`
  return `${units.toLocaleString()} of ${budget.toLocaleString()} units (${pct}%). ${reset}`
}

const exhaustedLine = (iso: string): string => {
  const at = ptTimeOf(iso)
  return `Google's daily quota ran out${at != null ? ` at ${at}` : ''}. Every YouTube chat stopped. People can press Start again after the reset.`
}

const countsTowardOf = (s: YouTubeLiveSession): { text: string, title: string | undefined } => {
  const app = s.countsTowardAppLimit === true
  const acct = s.countsTowardAccountLimit === true
  const reasons: string[] = []
  if (!app && s.demoExempt === true) reasons.push('Demo exempt: outside the app-wide limit.')
  if (!acct && s.foreignEvent === true) reasons.push('Staff-added source: outside the account limit.')
  const title = reasons.length > 0 ? reasons.join(' ') : undefined
  if (app && acct) return { text: 'App-wide and account limits', title }
  if (app) return { text: 'App-wide limit', title }
  if (acct) return { text: 'Account limit', title }
  return { text: 'No limits', title }
}

const scopeLabel = (scope: string | null): string => {
  if (scope === 'EVENT') return 'Stream'
  if (scope === 'ACCOUNT') return 'Account'
  return scope ?? 'Unknown'
}

const channelNameOf = (s: YouTubeLiveSession): string =>
  s.channelTitle ?? s.channelId ?? 'Unknown channel'

const SessionRow = ({
  s,
  busy,
  disabled,
  onStop
}: {
  s: YouTubeLiveSession
  busy: boolean
  disabled: boolean
  onStop: () => void
}): JSX.Element => {
  const counts = countsTowardOf(s)
  const canStop = s.accountId != null && s.channelId != null && s.sessionId != null
  return (
    <tr data-testid={`yt-live-session-${s.sessionId ?? ''}`} className='border-b border-white/5 align-top text-dm-text-secondary'>
      <td className='py-2 pr-3'>
        <span className='block max-w-[14rem] truncate font-medium text-dm-text-primary' title={channelNameOf(s)}>{channelNameOf(s)}</span>
        {s.stale === true && (
          <span data-testid='yt-live-session-stale' className='mt-0.5 inline-flex rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-300 ring-1 ring-inset ring-amber-500/30'>
            Not reading
          </span>
        )}
      </td>
      <td className={`py-2 pr-3 ${s.accountEmail == null ? 'italic text-dm-text-accent' : ''}`}>{s.accountEmail ?? 'unknown user'}</td>
      <td className={`py-2 pr-3 ${s.startedByEmail == null ? 'italic text-dm-text-accent' : ''}`}>{s.startedByEmail ?? 'unknown user'}</td>
      <td className='py-2 pr-3'>
        {s.scope === 'EVENT' && s.eventId != null
          ? (
            <Link to={eventPath(s.eventId)} className='inline-block max-w-[12rem] truncate text-dm-text-secondary underline-offset-2 hover:text-brand hover:underline' data-testid='yt-live-session-feeds'>
              {s.eventName ?? 'Untitled stream'}
            </Link>
            )
          : <span data-testid='yt-live-session-feeds'>Chat overlays</span>}
      </td>
      <td className='py-2 pr-3'>{scopeLabel(s.scope)}</td>
      <td className='py-2 pr-3 whitespace-nowrap'>{s.startedAt != null ? formatLocalDateTime(s.startedAt) : 'Unknown'}</td>
      <td className='py-2 pr-3 whitespace-nowrap'>{s.lastReadAt != null ? formatLocalDateTime(s.lastReadAt) : 'Not yet'}</td>
      <td className='py-2 pr-3'>{(s.unitsUsed ?? 0).toLocaleString()}</td>
      <td className='py-2 pr-3 whitespace-nowrap' data-testid='yt-live-session-speed'>
        {speedLevelLabel(s.level)}
        {s.hot === true && <span className='ml-1.5 text-emerald-300'>votes open</span>}
      </td>
      <td className='py-2 pr-3' title={counts.title}>{counts.text}</td>
      <td className='py-2 text-right'>
        {canStop && (
          <button
            type='button'
            onClick={onStop}
            disabled={disabled}
            data-testid={`yt-live-session-stop-${s.sessionId ?? ''}`}
            className='inline-flex items-center gap-1 rounded-md border border-red-500/30 px-2 py-1 text-[11px] font-medium text-red-300 hover:border-red-500/50 hover:bg-red-500/10 disabled:cursor-not-allowed disabled:opacity-60'
          >
            {busy ? <LoadingSpinner /> : <StopCircleIcon className='size-3.5' aria-hidden='true' />}
            Stop
          </button>
        )}
      </td>
    </tr>
  )
}

type Busy = 'loading' | 'saving' | `stopping:${string}` | null

export const YouTubeSpeedPanel = ({ auth0Token }: { auth0Token: string }): JSX.Element => {
  const [panel, setPanel] = useState<YouTubeSpeedPanelData | null>(null)
  const [form, setForm] = useState<FormState | null>(null)
  const [busy, setBusy] = useState<Busy>('loading')
  const [loadError, setLoadError] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [stopError, setStopError] = useState<string | null>(null)
  const raceRef = useRef(createRequestRace())
  const inFlightRef = useRef(false)
  const stoppedIdsRef = useRef<Set<string>>(new Set())
  const aliveRef = useRef(true)
  useEffect(() => {
    aliveRef.current = true
    return () => { aliveRef.current = false }
  }, [])

  const load = useCallback(async (reset: boolean): Promise<void> => {
    const id = raceRef.current.begin()
    setBusy('loading')
    setLoadError(null)
    try {
      const { panel: fetched } = await graphqlRequest<{ panel: YouTubeSpeedPanelData | null }>(
        getYouTubeSpeedPanelQuery, {}, auth0Token
      )
      if (!aliveRef.current || !raceRef.current.isCurrent(id)) return
      if (fetched == null) {
        setLoadError(LOAD_ERROR_COPY)
        return
      }
      setPanel(withoutStopped(fetched, stoppedIdsRef.current))
      setForm((prev) => adoptPanel(prev, fetched, reset))
    } catch {
      if (aliveRef.current && raceRef.current.isCurrent(id)) setLoadError(LOAD_ERROR_COPY)
    } finally {
      if (aliveRef.current && raceRef.current.isCurrent(id)) setBusy(null)
    }
  }, [auth0Token])

  useEffect(() => {
    void load(true)
  }, [load])

  const edit = <K extends DraftKey>(key: K, value: Draft[K]): void => {
    setSaved(false)
    setSaveError(null)
    setForm((prev) => prev == null ? prev : { ...prev, values: { ...prev.values, [key]: value }, touched: { ...prev.touched, [key]: true } })
  }

  const handleSave = async (): Promise<void> => {
    if (form == null || busy != null || inFlightRef.current) return
    const snap = snapshotOf(form)
    if ('error' in snap) {
      setSaved(false)
      setSaveError(snap.error)
      return
    }
    inFlightRef.current = true
    const id = raceRef.current.begin()
    setBusy('saving')
    setSaveError(null)
    setSaved(false)
    try {
      const { panel: fresh } = await graphqlRequest<{ panel: YouTubeSpeedPanelData | null }>(
        setYouTubeSpeedSettingsMutation, { ...snap.variables }, auth0Token
      )
      if (!aliveRef.current || !raceRef.current.isCurrent(id)) return
      if (fresh != null) {
        setPanel(withoutStopped(fresh, stoppedIdsRef.current))
        setForm((prev) => adoptPanel(prev, fresh, true))
      }
      setSaved(true)
    } catch (err) {
      if (aliveRef.current && raceRef.current.isCurrent(id)) setSaveError(err instanceof Error ? err.message : 'Failed to save the YouTube speed settings')
    } finally {
      inFlightRef.current = false
      if (aliveRef.current && raceRef.current.isCurrent(id)) setBusy(null)
    }
  }

  const handleStop = async (s: YouTubeLiveSession): Promise<void> => {
    if (busy != null || inFlightRef.current || s.accountId == null || s.channelId == null || s.sessionId == null) return
    const frees = s.countsTowardAppLimit === true ? ' and frees its slot' : ''
    const ok = globalThis.confirm(`Stop the YouTube chat for ${channelNameOf(s)}? It stops reading at once${frees}.`)
    if (!ok) return
    inFlightRef.current = true
    setBusy(`stopping:${s.sessionId}`)
    setStopError(null)
    let stopped = false
    try {
      const { result } = await graphqlRequest<{ result: YouTubeStaffStopResult | null }>(
        stopYouTubeSessionAsStaffMutation,
        { accountId: s.accountId, channelId: s.channelId, sessionId: s.sessionId },
        auth0Token
      )
      const still = result?.session
      if (result != null && !(still != null && still.sessionId === s.sessionId && still.status === 'RUNNING')) {
        stoppedIdsRef.current.add(s.sessionId)
        if (aliveRef.current) setPanel((prev) => prev == null ? prev : withoutStopped(prev, stoppedIdsRef.current))
      }
      stopped = true
    } catch (err) {
      if (aliveRef.current) setStopError(err instanceof Error ? err.message : 'Failed to stop the YouTube chat')
    } finally {
      inFlightRef.current = false
      if (aliveRef.current) setBusy(null)
    }
    if (stopped && aliveRef.current) await load(false)
  }

  const nowMs = Date.now()
  const loading = busy === 'loading'
  const disabled = busy != null

  if (panel == null || form == null) {
    return (
      <div data-testid='youtube-speed-panel' className='rounded-xl border border-white/5 bg-dm-bg-secondary p-6'>
        <h2 className='text-lg font-semibold text-dm-text-primary'>YouTube speed and limits</h2>
        {loadError != null
          ? (
            <div className='mt-4 flex items-center gap-3 text-sm text-red-300' data-testid='yt-speed-error'>

              <span>{loadError}</span>
              <button
                type='button'
                onClick={() => { void load(true) }}
                disabled={loading}
                data-testid='yt-speed-retry'
                className='rounded-md border border-red-500/30 px-3 py-1 text-xs font-medium hover:bg-red-500/20 disabled:cursor-not-allowed disabled:opacity-60'
              >
                Try again
              </button>
            </div>
            )
          : (
            <div className='mt-4 flex items-center py-2' data-testid='yt-speed-loading'>
              <LoadingSpinner />
            </div>
            )}
      </div>
    )
  }

  const p = panel
  const v = form.values
  const liveOk = liveAllowedFor(form, p)
  const sessions = p.sessions ?? []
  const appMax = p.appChatLimitMax ?? LIMIT_MAX
  const holdback = p.demoHoldbackSlots ?? 0
  const dirty = Object.keys(form.touched).length > 0

  return (
    <div data-testid='youtube-speed-panel' className='flex flex-col gap-5'>

      <section data-testid='yt-speed-card' className='rounded-xl border border-white/5 bg-dm-bg-secondary p-6'>
        <div className='flex items-center justify-between gap-3'>
          <h2 className='text-lg font-semibold text-dm-text-primary'>YouTube speed and limits</h2>
          <RefreshButton onClick={() => { void load(true) }} busy={loading} disabled={disabled} testid='yt-speed-refresh' />
        </div>
        <p className='mt-1 text-xs text-dm-text-secondary'>
          How often every YouTube chat is read, app-wide. Only superusers see or change speed.
          Google&apos;s suggested wait is always respected.
        </p>

        {loadError != null && (
          <div className='mt-3' data-testid='yt-speed-error'>
            <ErrorInline onDismiss={() => setLoadError(null)}>{loadError}</ErrorInline>
          </div>
        )}

        <div className='mt-4 rounded-md border border-white/5 bg-dm-bg-accent/30 p-3 text-xs'>
          <p className='text-dm-text-accent'>
            Today{p.pacificDay != null ? ` (Pacific day ${p.pacificDay})` : ''}
          </p>
          <p className='mt-0.5 text-sm text-dm-text-primary' data-testid='yt-speed-today'>{todayLine(p, nowMs)}</p>
          <p className={`mt-1 ${p.brakeCapLevel != null && p.brakeEnabled !== false ? 'text-amber-300' : 'text-dm-text-secondary'}`} data-testid='yt-speed-brake-state'>
            {brakeLine(p)}
          </p>
          {p.quotaExhaustedAt != null && (
            <p className='mt-1 text-red-300' data-testid='yt-speed-exhausted'>{exhaustedLine(p.quotaExhaustedAt)}</p>
          )}
        </div>

        <div className='mt-5 grid grid-cols-1 gap-5 md:grid-cols-2'>

          <div className='flex min-w-0 flex-col gap-1'>
            <label htmlFor='yt-speed-level' className='text-xs font-medium text-dm-text-secondary'>Default speed</label>
            <select
              id='yt-speed-level'
              value={v.level}
              onChange={(e) => edit('level', e.target.value)}
              disabled={disabled}
              data-testid='yt-speed-level'
              className='w-full rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand disabled:opacity-60'
            >
              {SETTABLE_SPEED_LEVELS.map((level) => (
                <option key={level} value={level} disabled={level === 'LIVE' && liveOk === false && v.level !== 'LIVE'}>
                  {LEVEL_LABEL[level]}
                </option>
              ))}
            </select>
            {liveOk === false && (
              <p className='text-[11px] text-dm-text-accent' data-testid='yt-speed-live-note'>{LIVE_BUDGET_COPY}</p>
            )}
            <ul className='mt-2 space-y-0.5 text-[11px] text-dm-text-accent' data-testid='yt-speed-legend'>
              {LEVEL_LEGEND.map(([level, text]) => (
                <li key={level}>
                  <span className='font-medium text-dm-text-secondary'>{LEVEL_LABEL[level]}:</span> {text}
                </li>
              ))}
            </ul>
            <p className='mt-1 text-[11px] text-dm-text-accent'>
              Votes are open while a poll, tug or trivia question that counts YouTube votes is running,
              for up to 15 minutes after each Start. A stream can have its own speed on its Integrations page.
            </p>
          </div>

          <div className='flex min-w-0 flex-col gap-3'>
            <label className='flex items-start gap-3'>
              <input
                type='checkbox'
                role='switch'
                checked={v.brake}
                onChange={(e) => edit('brake', e.target.checked)}
                disabled={disabled}
                data-testid='yt-speed-brake'
                className='mt-0.5 size-4 shrink-0 accent-brand'
              />
              <span className='min-w-0'>
                <span className='block text-xs font-medium text-dm-text-secondary'>Budget brake</span>
                <span className='block text-[11px] text-dm-text-accent'>
                  When today&apos;s units pass 60, 75, 90 and 97 percent of the daily budget, every YouTube chat
                  slows to Fast, Standard, Economy and then Saver until midnight Pacific. It only ever slows down
                  within a day.
                </span>
              </span>
            </label>
            <NumberField
              id='yt-speed-budget'
              label='Daily budget (units)'
              value={v.budget}
              onChange={(x) => edit('budget', x)}
              placeholder={p.dailyBudgetSaved === true ? '' : `Server setting: ${fmtCount(p.dailyBudgetUnits)}`}
              hint={fallbackHint(p.dailyBudgetSaved === true, p.dailyBudgetUnits, ' units')}
              disabled={disabled}
              testid='yt-speed-budget'
            />
          </div>

          <NumberField
            id='yt-speed-app-limit'
            label={`App-wide chat limit (0 to ${appMax})`}
            value={v.appLimit}
            onChange={(x) => edit('appLimit', x)}
            placeholder={p.appChatLimitSaved === true ? '' : `Server setting: ${fmtCount(p.appChatLimit)}`}
            hint={fallbackHint(p.appChatLimitSaved === true, p.appChatLimit, '')}
            disabled={disabled}
            testid='yt-speed-app-limit'
          />
          <NumberField
            id='yt-speed-account-limit'
            label='Per-account chat limit (0 to 8)'
            value={v.accountLimit}
            onChange={(x) => edit('accountLimit', x)}
            placeholder={p.accountChatLimitSaved === true ? '' : `Server setting: ${fmtCount(p.accountChatLimit)}`}
            hint={fallbackHint(p.accountChatLimitSaved === true, p.accountChatLimit, '')}
            disabled={disabled}
            testid='yt-speed-account-limit'
          />
          <NumberField
            id='yt-speed-holdback'
            label='Demo hold-back slots (0 to 8)'
            value={v.holdback}
            onChange={(x) => edit('holdback', x)}
            placeholder='0'
            hint='Slots of the app-wide limit kept free for the demo account. 0 holds none back.'
            disabled={disabled}
            testid='yt-speed-holdback'
          />
        </div>

        {p.appChatLimit != null && p.appChatLimit > appMax && (
          <p className='mt-3 text-xs text-amber-300' data-testid='yt-speed-alarm-note'>
            The server setting allows more than {appMax} chats at once. The poller alarm must be revisited
            before running above {appMax}.
          </p>
        )}
        {p.accountChatLimit != null && p.accountChatLimit > LIMIT_MAX && (
          <p className='mt-3 text-xs text-amber-300' data-testid='yt-speed-account-alarm-note'>
            The server setting allows more than {LIMIT_MAX} chats for one account. The poller alarm must be
            revisited before running above {LIMIT_MAX} per account.
          </p>
        )}

        <div className='mt-5 flex flex-wrap items-center gap-3'>
          <button
            type='button'
            onClick={() => { void handleSave() }}
            disabled={disabled}
            data-testid='yt-speed-save'
            className='inline-flex items-center gap-1.5 rounded-md bg-brand px-4 py-2 text-sm font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-60'
          >
            {busy === 'saving' && <LoadingSpinner />}
            Save
          </button>
          {dirty && (
            <button
              type='button'
              onClick={() => { setSaveError(null); setForm(adoptPanel(form, p, true)) }}
              disabled={disabled}
              data-testid='yt-speed-discard'
              className='text-xs font-medium text-dm-text-secondary underline-offset-2 hover:text-dm-text-primary hover:underline disabled:cursor-not-allowed disabled:opacity-60'
            >
              Discard changes
            </button>
          )}
          {saved && !dirty && <span className='text-xs text-emerald-300' data-testid='yt-speed-saved'>Saved.</span>}
          <span className='min-w-0 break-words text-[11px] text-dm-text-accent' data-testid='yt-speed-last-saved'>
            {p.updatedAt != null
              ? `Last saved by ${p.updatedByEmail ?? 'unknown user'} at ${formatLocalDateTime(p.updatedAt)}.`
              : 'Not saved yet: the defaults and server settings apply.'}
          </span>
        </div>
        {saveError != null && (
          <div className='mt-3' data-testid='yt-speed-save-error'>
            <ErrorInline onDismiss={() => setSaveError(null)}>{saveError}</ErrorInline>
          </div>
        )}
      </section>

      <section data-testid='yt-live-card' className='rounded-xl border border-white/5 bg-dm-bg-secondary p-6'>
        <div className='flex items-center justify-between gap-3'>
          <h2 className='text-lg font-semibold text-dm-text-primary'>Live YouTube sessions</h2>
          <RefreshButton onClick={() => { void load(true) }} busy={loading} disabled={disabled} testid='yt-live-refresh' />
        </div>
        <p className='mt-1 text-sm text-dm-text-primary' data-testid='yt-live-summary'>
          {sessions.length.toLocaleString()} running. {fmtCount(p.slotsInUse)} of {fmtCount(p.appChatLimit)} slots in use
          {holdback > 0 ? ` (${holdback} held back for the demo).` : ' (none held back for the demo).'}
        </p>
        {stopError != null && (
          <div className='mt-3' data-testid='yt-live-stop-error'>
            <ErrorInline onDismiss={() => setStopError(null)}>{stopError}</ErrorInline>
          </div>
        )}
        {sessions.length === 0
          ? (
            <p className='mt-3 rounded-lg border border-dashed border-white/10 px-4 py-6 text-center text-xs text-dm-text-secondary' data-testid='yt-live-empty'>
              No YouTube chats are running.
            </p>
            )
          : (
            <div className='relative mt-3 overflow-x-auto'>
              <table className='w-full min-w-[960px] text-left text-[11px]' data-testid='yt-live-table'>
                <thead>
                  <tr className='border-b border-white/10 text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent'>
                    <th className='py-1.5 pr-3'>Channel</th>
                    <th className='py-1.5 pr-3'>Account</th>
                    <th className='py-1.5 pr-3'>Started by</th>
                    <th className='py-1.5 pr-3'>Feeds</th>
                    <th className='py-1.5 pr-3'>Scope</th>
                    <th className='py-1.5 pr-3'>Started</th>
                    <th className='py-1.5 pr-3'>Last read</th>
                    <th className='py-1.5 pr-3'>Units</th>
                    <th className='py-1.5 pr-3'>Speed</th>
                    <th className='py-1.5 pr-3'>Counts toward</th>
                    <th className='py-1.5'><span className='sr-only'>Stop</span></th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((s, i) => (
                    <SessionRow
                      key={s.sessionId ?? `row-${i}`}
                      s={s}
                      busy={busy === `stopping:${s.sessionId ?? ''}`}
                      disabled={disabled}
                      onStop={() => { void handleStop(s) }}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            )}
      </section>
    </div>
  )
}
